/** Console v0.5 Team and invites: session A's routes (#367). Every rule is the server's: an account admin only,
 * the account's limits, 7-day single-use links, password + authenticator before any data. */

import { apiRequest } from "../api.js";
import type { SignInStep } from "../api.js";
import type { Capability, Delivery, Invite } from "./team-logic.js";

export interface InviteResult {
  invite: Invite;
  delivery?: Delivery;
  test_accept_url?: string; // TEST only, *@preekie.test only
}

export function fetchInvites(): Promise<{ invites: Invite[] }> {
  return apiRequest("GET", "/api/team/invites", "Preekie couldn't load the invites.");
}

export function createInvite(body: { email: string; capabilities: Capability[]; organisation_ids: string[] | null }): Promise<InviteResult> {
  return apiRequest("POST", "/api/team/invites", "The invite wasn't sent.", body);
}

export function resendInvite(inviteId: string): Promise<InviteResult> {
  return apiRequest("POST", `/api/team/invites/${encodeURIComponent(inviteId)}/resend`, "The invite wasn't sent again.");
}

export function revokeInvite(inviteId: string): Promise<InviteResult> {
  return apiRequest("POST", `/api/team/invites/${encodeURIComponent(inviteId)}/revoke`, "The invite wasn't cancelled.");
}

// ---- The accept page: no session and no CSRF token; the token (from the link's #fragment) is the secret ----

export interface InviteInfo {
  account_name: string;
  email: string;
  expires_at: string;
  password_rules?: unknown;
}

/** The token goes in the body, never the URL (D, #368): POST even for the lookup. */
export function fetchInviteInfo(token: string): Promise<InviteInfo> {
  return apiRequest("POST", "/api/invite/lookup", "Preekie couldn't open this invite.", { token }, true);
}

export type MfaSetupChallenge = Extract<SignInStep, { challenge: "MFA_SETUP" }>;

export function acceptInvite(token: string, password: string, displayName: string): Promise<MfaSetupChallenge> {
  const body: { token: string; password: string; display_name?: string } = { token, password };
  if (displayName.trim()) body.display_name = displayName.trim();
  return apiRequest("POST", "/api/invite/accept", "Your password wasn't set.", body, true);
}

// ---- People, approval rules and limits (A's #373, contracts s.11). Account admin only; every change is a POST ----

export interface Person {
  user_id: string;
  email: string | null; // null for people who joined before invites existed
  display_name: string | null;
  capabilities: Capability[];
  organisation_ids: string[] | null; // null = all organisations, including new ones
  status: string;
  two_factor: "required";
  is_you: boolean;
  member_since: string;
}

export interface TeamData {
  people: Person[];
  invites: Invite[];
  service_grants: ServiceGrant[]; // CAP-009.B5
  rules: { self_approval_allowed: boolean };
  limits: { organisations: { used: number; max: number }; users: { used: number; max: number } } | null;
}

export function fetchTeam(): Promise<TeamData> {
  return apiRequest("GET", "/api/team", "Preekie couldn't load your team.");
}

export function updateMember(userId: string, change: { capabilities?: Capability[]; organisation_ids?: string[] | null }): Promise<{ person: Person }> {
  return apiRequest("POST", `/api/team/members/${encodeURIComponent(userId)}`, "The changes weren't saved.", change);
}

export function removeMember(userId: string): Promise<{ removed: true }> {
  return apiRequest("POST", `/api/team/members/${encodeURIComponent(userId)}/remove`, "They weren't removed.", {});
}

export function setRules(selfApprovalAllowed: boolean): Promise<{ rules: { self_approval_allowed: boolean } }> {
  return apiRequest("POST", "/api/team/rules", "The approval rule wasn't changed.", { self_approval_allowed: selfApprovalAllowed });
}

// ---- Preekie service-team access (A's #382, contracts s.16). Off by default; the account admin grants it, scoped and
// time-limited, and can withdraw it at once. The service team can never approve or lodge. ----

export interface ServiceGrant {
  grant_id: string;
  user_id: string;
  display_name: string | null;
  organisation_ids: string[];
  services: string[];
  status: "active" | "expired" | "withdrawn";
  granted_at: string;
  expires_at: string;
  withdrawn_at: string | null;
}

export function fetchServiceStaff(): Promise<{ staff: Array<{ user_id: string; display_name: string }> }> {
  return apiRequest("GET", "/api/team/service-staff", "Preekie couldn't load the service team.");
}

export function grantServiceAccess(body: { user_id: string; organisation_ids: string[]; services: string[]; days: number }): Promise<{ grant: ServiceGrant }> {
  return apiRequest("POST", "/api/team/service-grants", "Access wasn't granted.", body);
}

export function withdrawServiceAccess(grantId: string, userId: string): Promise<{ withdrawn: true }> {
  return apiRequest("POST", `/api/team/service-grants/${encodeURIComponent(grantId)}/withdraw`, "Access wasn't withdrawn.", { user_id: userId });
}
